import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ShieldCheck, Send, Gem } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { H, Eyebrow, Section } from "@/components/site/SiteSections";

const field = "w-full rounded-xl border px-4 h-12 bg-transparent outline-none focus:ring-2 text-sm";
const fieldStyle = { borderColor: "var(--line)", "--tw-ring-color": "var(--accent)" };

export function Products({ products, onEnquire }) {
  const cats = useMemo(() => ["All", ...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const [cat, setCat] = useState("All");
  const shown = cat === "All" ? products : products.filter((p) => p.category === cat);
  return (
    <Section id="collection" testId="site-collection">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div><Eyebrow>Our collection</Eyebrow><H>Crafted to be treasured</H></div>
        {cats.length > 2 && (
          <div className="flex flex-wrap gap-2" data-testid="site-category-filters">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`site-category-${c.toLowerCase().replace(/\W/g, "-")}`}
                className="px-4 py-1.5 rounded-full text-xs border transition-colors"
                style={{ borderColor: "var(--accent)", background: cat === c ? "var(--accent)" : "transparent", color: cat === c ? "#fff" : "var(--text)" }}>{c}</button>
            ))}
          </div>
        )}
      </div>
      {!shown.length ? (
        <p className="text-sm py-10 text-center" style={{ color: "var(--muted)" }} data-testid="site-collection-empty">Our new collection is coming soon. Visit the showroom to see it in person.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((p, i) => (
            <article key={p.id} className="group rounded-2xl overflow-hidden border" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`site-product-${i}`}>
              <div className="aspect-square overflow-hidden" style={{ background: "var(--bg)" }}>
                {p.image ? <img src={assetUrl(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  : <div className="w-full h-full grid place-items-center" style={{ color: "var(--accent)" }}><Gem size={40} /></div>}
              </div>
              <div className="p-5">
                {p.category && <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--accent)" }}>{p.category}</p>}
                <h3 className="text-xl mt-1" style={{ fontFamily: "var(--hfont)" }}>{p.name}</h3>
                <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>{[p.metal, p.weight].filter(Boolean).join(" · ")}</p>
                {p.description && <p className="text-sm mt-3 line-clamp-2" style={{ color: "var(--muted)" }}>{p.description}</p>}
                <div className="flex items-center justify-between mt-4">
                  <span className="font-semibold">{p.price || "Price on request"}</span>
                  <button onClick={() => onEnquire(p.name)} className="text-xs font-semibold underline underline-offset-4" style={{ color: "var(--accent)" }} data-testid={`site-product-enquire-${i}`}>Enquire</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </Section>
  );
}

export function CertCheck({ site, base }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get(`/public/site/${site.slug}/lookup`, { params: { cert_no: q } });
      nav(`${base}/certificate/${data.record_id}`);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Section id="verify" alt testId="site-verify">
      <div className="max-w-2xl mx-auto text-center">
        <ShieldCheck size={36} className="mx-auto" style={{ color: "var(--accent)" }} />
        <Eyebrow><span className="block mt-4">Authenticity check</span></Eyebrow>
        <H>Verify your jewellery certificate</H>
        <p className="mt-4 text-sm" style={{ color: "var(--muted)" }}>Scan the QR code on your card, or enter the certificate number printed on it.</p>
        <form onSubmit={submit} className="mt-8 flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. JWC-2026-00001" className={`${field} font-mono`} style={fieldStyle} data-testid="site-cert-input" />
          <button disabled={busy || !q.trim()} className="h-12 px-6 rounded-xl text-sm font-semibold flex items-center gap-2 shrink-0 disabled:opacity-50" style={{ background: "var(--accent)", color: "#fff" }} data-testid="site-cert-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Verify
          </button>
        </form>
        {err && <p className="mt-3 text-sm text-red-500" data-testid="site-cert-error">{err}</p>}
      </div>
    </Section>
  );
}

export function Enquiry({ site, product, setProduct }) {
  const [f, setF] = useState({ name: "", phone: "", message: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    setErr("");
    try {
      await api.post(`/public/site/${site.slug}/enquiry`, { ...f, product });
      setState("done");
      setF({ name: "", phone: "", message: "" });
      setProduct("");
    } catch (e2) { setErr(errMsg(e2)); setState("idle"); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Section id="enquiry" testId="site-enquiry">
      <div className="grid md:grid-cols-[1fr_1.2fr] gap-12">
        <div><Eyebrow>Enquiry</Eyebrow><H>Ask us anything</H><p className="mt-4 text-sm" style={{ color: "var(--muted)" }}>Custom designs, bridal sets, exchange or today's price — leave your number and we'll call you back.</p></div>
        {state === "done" ? (
          <div className="rounded-2xl border p-8 text-center" style={{ borderColor: "var(--line)", background: "var(--surface)" }} data-testid="site-enquiry-success">
            <p className="text-2xl" style={{ fontFamily: "var(--hfont)" }}>Thank you!</p>
            <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>We've received your enquiry and will contact you shortly.</p>
            <button onClick={() => setState("idle")} className="mt-4 text-xs underline" style={{ color: "var(--accent)" }}>Send another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {product && <p className="text-xs" data-testid="site-enquiry-product">About: <b>{product}</b> <button type="button" onClick={() => setProduct("")} className="underline ml-1">remove</button></p>}
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="Your name" value={f.name} onChange={set("name")} className={field} style={fieldStyle} data-testid="site-enquiry-name" />
              <input required placeholder="Mobile number" value={f.phone} onChange={set("phone")} className={field} style={fieldStyle} data-testid="site-enquiry-phone" />
            </div>
            <textarea rows={4} placeholder="Your message" value={f.message} onChange={set("message")} className={`${field} h-auto py-3`} style={fieldStyle} data-testid="site-enquiry-message" />
            {err && <p className="text-sm text-red-500" data-testid="site-enquiry-error">{err}</p>}
            <button disabled={state === "busy"} className="h-12 px-8 rounded-full text-sm font-semibold flex items-center gap-2" style={{ background: "var(--accent)", color: "#fff" }} data-testid="site-enquiry-submit">
              {state === "busy" ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Send enquiry
            </button>
          </form>
        )}
      </div>
    </Section>
  );
}
